/**
 * Sliding renewal of a session token - LIQUIDO's team token and Polly's session token alike.
 *
 * Before a request, each app's client asks renewIfDue(). Is the token due, it sends `mutation renewSession` and stores
 * the fresh token it gets back - beside the request, never in its way: nothing here is awaited. The backend reads
 * everything again for the fresh token, and never lets a session last longer than its maximum after the real login
 * (30 days LIQUIDO, 90 days a Polly passkey). See the backend's docs/liquido-architecture.md §6.
 *
 * This module knows no storage and no backend: each app passes how to read and store its token and how to renew it,
 * because src/shared imports no app (docs/liquido-architecture.md §2.1). The token's lifetime is read from the token
 * itself (exp - iat), so nothing here needs to know the backend's settings.
 *
 * An expired token is never renewed: the backend refuses it with HTTP 401, and each app handles that as before (the
 * login page). Neither is a token without iat and exp - the mocks' made-up ones.
 */
import { decodeJwtPayload, isJwtExpired } from "./jwt-payload.js"

/** After a network failure, the same token is tried again only this much later */
export const RETRY_AFTER_MS = 5 * 60 * 1000
/** After a renewal, the next one waits at least this long - in case the device's clock runs far ahead */
export const MIN_INTERVAL_MS = 5 * 60 * 1000

/** iat and exp of a JWT, in seconds - or undefined when it lacks one of them */
function timesOf(jwt) {
	const { iat, exp } = decodeJwtPayload(jwt) || {}
	return typeof iat === "number" && typeof exp === "number" ? { iat, exp } : undefined
}

/** LIQUIDO: due once the token is past half its lifetime */
export function isPastHalfLife(jwt, nowMs = Date.now()) {
	const times = timesOf(jwt)
	return !!times && nowMs / 1000 >= times.iat + (times.exp - times.iat) / 2
}

/** Polly: due once the token is older than this - every visit after that gives the full lifetime again */
export function isOlderThan(secs) {
	return (jwt, nowMs = Date.now()) => {
		const times = timesOf(jwt)
		return !!times && nowMs / 1000 - times.iat >= secs
	}
}

/**
 * @param {Object} options
 * @param {Function} options.getJwt () => the session's token now, or undefined
 * @param {Function} options.setJwt (freshJwt) => store it as the session's token
 * @param {Function} options.renew (jwt) => Promise of the fresh token - or of null when the backend refused to renew
 *        this token (then it is never tried again). Rejects only when the backend could not be reached.
 * @param {Function} options.isDue (jwt, nowMs) => is this token worth renewing now? isPastHalfLife or isOlderThan(secs)
 * @returns {{ renewIfDue: Function }} renewIfDue() => Promise of true when a fresh token was stored. Never rejects.
 */
export function createSessionRenewal({ getJwt, setJwt, renew, isDue }) {
	let inFlight          // { jwt, promise } - one renewal at a time
	let failed            // { jwt, at } - the last network failure
	const refused = new Set()   // tokens the backend refused to renew
	let lastSuccessAt = 0
	let finalExp          // a session at its maximum: renewing gives no later exp

	function renewIfDue(nowMs = Date.now()) {
		const jwt = getJwt()
		if (!jwt) return Promise.resolve(false)
		if (inFlight?.jwt === jwt) return inFlight.promise
		if (isJwtExpired(jwt, nowMs) || !isDue(jwt, nowMs)) return Promise.resolve(false)
		if (refused.has(jwt) || timesOf(jwt).exp === finalExp) return Promise.resolve(false)
		if (failed?.jwt === jwt && nowMs - failed.at < RETRY_AFTER_MS) return Promise.resolve(false)
		if (nowMs - lastSuccessAt < MIN_INTERVAL_MS) return Promise.resolve(false)

		// Claimed BEFORE renew() runs, which starts in a microtask: the renewal request passes the client's same entry
		// point - which then finds it in flight instead of starting a second one, and a second, and ...
		const entry = { jwt }
		inFlight = entry
		entry.promise = Promise.resolve()
			.then(() => renew(jwt))
			.then(fresh => {
				if (!fresh) {
					refused.add(jwt)
					return false
				}
				lastSuccessAt = Date.now()
				const oldExp = timesOf(jwt).exp
				const freshExp = timesOf(fresh)?.exp
				if (freshExp !== undefined && freshExp <= oldExp) finalExp = freshExp   // the session's maximum: no more renewals
				// Only if this is still the session: a login, a team switch, a logout, a passkey or another tab meanwhile wins
				if (getJwt() !== jwt) return false
				setJwt(fresh)
				return true
			})
			.catch(err => {
				console.warn("Cannot renew the session now", err)
				failed = { jwt, at: Date.now() }
				return false
			})
			.finally(() => {
				if (inFlight === entry) inFlight = undefined
			})
		return entry.promise
	}

	return { renewIfDue }
}
